<template>
    <div class="d-flex flex-column overflow-hidden h-100">
        <vue-custom-scrollbar
            class="app-content-padding flex-grow-1 overflow-hidden h-100"
        >
            <ts-page-title
                :title="$t('bankProfile.pageTitle')"
                :breadcrumb="[
                    { text: $t('home'), href: '/' },
                    {
                        text: $t('bankProfile.pageTitle'),
                        href: '/admin/bank-profiles'
                    },
                    { text: $t('edit'), active: true }
                ]"
            />

            <ts-panel>
                <ts-panel-wrapper>
                    <!-- Bank name (inline edit + own save) -->
                    <div class="row tw-mb-4">
                        <div class="col-md-6">
                            <label class="required form-col-label control-label">{{
                                $t("bankProfile.bankName")
                            }}</label>
                            <div class="tw-flex tw-items-center tw-gap-2">
                                <Input
                                    v-model="model.bank_name"
                                    :placeholder="$t('bankProfile.bankName')"
                                    :class="{ 'is-invalid': errors.has('bank_name') }"
                                />
                                <ts-button
                                    color="primary"
                                    :waiting="savingBank"
                                    :disabled="savingBank || !bankNameDirty"
                                    @click.prevent="onSaveBankName"
                                >
                                    {{ $t("save") }}
                                </ts-button>
                            </div>
                            <div class="invalid-feedback" v-if="errors.has('bank_name')">
                                {{ errors.first("bank_name") }}
                            </div>
                        </div>
                    </div>

                    <!-- Accounts -->
                    <div class="tw-flex tw-items-center tw-justify-between tw-mb-2 tw-gap-2">
                        <h6 class="tw-font-semibold tw-mb-0">
                            {{ $t("bankProfile.accounts") }}
                            <span class="ivu-tag ivu-tag-size-default ivu-tag-default tw-ml-2">{{ accounts.length }}</span>
                        </h6>
                        <div class="tw-flex tw-items-center tw-gap-2">
                            <Input
                                v-model="accountSearch"
                                search
                                clearable
                                size="small"
                                :placeholder="$t('search')"
                                style="width: 260px"
                            />
                            <Button size="small" type="primary" ghost @click="onAddAccount">
                                <i class="fas fa-plus tw-mr-1"></i>{{ $t("addNew") }}
                            </Button>
                        </div>
                    </div>

                    <Table
                        :columns="columns"
                        :data="pagedAccounts"
                        size="small"
                        :loading="loadingAccounts"
                        stripe
                        :no-data-text="$t('bankProfile.noAccounts')"
                    >
                        <template slot-scope="{ row }" slot="employee">
                            <span v-if="row.employee">
                                {{ row.employee.employee_name_en || row.employee.employee_name_kh }}
                            </span>
                            <span v-else class="tw-text-gray-300">—</span>
                        </template>
                        <template slot-scope="{ row }" slot="qr_image">
                            <img
                                v-if="row.qr_image"
                                :src="row.qr_image"
                                class="tw-w-8 tw-h-8 tw-object-cover tw-rounded tw-border tw-border-gray-200 tw-mx-auto"
                            />
                            <span v-else class="tw-text-gray-300">—</span>
                        </template>
                        <template slot-scope="{ row }" slot="action">
                            <a
                                href="#"
                                class="text-primary"
                                @click.prevent="onEditAccount(row)"
                                v-tooltip="$t('edit')"
                            >
                                <Icon type="ios-create" size="20" />
                            </a>
                            <Poptip
                                :title="$t('are_you_sure_to_delete')"
                                :transfer="true"
                                width="200"
                            >
                                <a
                                    class="ml-2 text-danger"
                                    v-tooltip="$t('delete')"
                                    :disabled="row._deleting"
                                >
                                    <i class="fas fa-circle-notch fa-spin" v-if="row._deleting"></i>
                                    <Icon type="ios-trash" size="20" v-else />
                                </a>
                                <div slot="content" class="tw-text-right tw-mt-2">
                                    <a
                                        class="ivu-btn ivu-btn-danger ivu-btn-small"
                                        @click.prevent="onDeleteAccount(row)"
                                    >
                                        <span>{{ $t("yes") }}</span>
                                    </a>
                                </div>
                            </Poptip>
                        </template>
                    </Table>

                    <div class="tw-flex tw-items-center tw-justify-end tw-p-4" v-if="filteredAccounts.length > perPage">
                        <Page
                            :current="currentPage"
                            :total="filteredAccounts.length"
                            :page-size="perPage"
                            :page-size-opts="[15, 30, 50, 100]"
                            show-sizer
                            show-total
                            @on-change="p => (currentPage = p)"
                            @on-page-size-change="s => { perPage = s; currentPage = 1; }"
                        />
                    </div>
                </ts-panel-wrapper>
            </ts-panel>
        </vue-custom-scrollbar>

        <Spin size="large" fix v-if="loading"></Spin>

        <div
            id="footer"
            class="app-footer m-0 tw-flex tw-justify-end tw-space-x-2"
            style="background-color: #e9ecef"
        >
            <ts-button
                @click.prevent="$router.push({ name: 'bank-profile' })"
                class="btn-gray"
            >
                {{ $t("close") }}
            </ts-button>
        </div>

        <account-form-modal
            v-if="model.bank_id"
            v-model="accountModal"
            :bank-id="model.bank_id"
            :bank-name="model.bank_name"
            :account="editingAccount"
            @saved="onAccountSaved"
        />
    </div>
</template>

<script>
import AppOptions from "@/config/AppOptions.vue";
import { Errors } from "form-backend-validation";
import { mapActions, mapState } from "vuex";
import { isEmpty } from "lodash";
import AccountFormModal from "./components/account-form-modal.vue";

export default {
    name: "BankProfileEdit",
    components: { AccountFormModal },
    data() {
        return {
            loading: false,
            savingBank: false,
            loadingAccounts: false,
            errors: new Errors(),
            model: {
                bank_id: null,
                bank_name: "",
            },
            originalBankName: "",
            accounts: [],
            accountSearch: "",
            currentPage: 1,
            perPage: 15,
            accountModal: false,
            editingAccount: null
        };
    },
    created() {
        AppOptions.appContentFullHeight = true;
        AppOptions.appContentClass = "p-0";
    },
    computed: {
        ...mapState("finance/bankProfile", ["edit_data"]),
        bankNameDirty() {
            return (this.model.bank_name || "").trim() !== (this.originalBankName || "").trim();
        },
        filteredAccounts() {
            const q = (this.accountSearch || "").trim().toLowerCase();
            if (!q) return this.accounts;
            return this.accounts.filter(a => {
                const empName = a.employee
                    ? (a.employee.employee_name_en || a.employee.employee_name_kh || "")
                    : "";
                return (
                    (a.account_no || "").toLowerCase().includes(q) ||
                    (a.account_name || "").toLowerCase().includes(q) ||
                    empName.toLowerCase().includes(q)
                );
            });
        },
        pagedAccounts() {
            const start = (this.currentPage - 1) * this.perPage;
            return this.filteredAccounts.slice(start, start + this.perPage);
        },
        columns() {
            return [
                {
                    title: this.$t("bankProfile.employeeId"),
                    slot: "employee",
                    minWidth: 180
                },
                {
                    title: this.$t("bankProfile.accountNo"),
                    key: "account_no",
                    minWidth: 160
                },
                {
                    title: this.$t("bankProfile.accountName"),
                    key: "account_name",
                    minWidth: 200
                },
                {
                    title: this.$t("bankProfile.qrImage"),
                    slot: "qr_image",
                    width: 80,
                    align: "center"
                },
                {
                    title: this.$t("bankProfile.isReceiveCashTransfer"),
                    key: "is_receive_cash_transfer",
                    width: 140,
                    align: "center",
                    render: (h, { row }) =>
                        h("i-switch", {
                            props: {
                                value: row.is_receive_cash_transfer,
                                size: "small",
                                disabled: true
                            }
                        })
                },
                {
                    title: this.$t("actions"),
                    slot: "action",
                    align: "center",
                    width: 110
                }
            ];
        }
    },
    watch: {
        accountSearch() {
            this.currentPage = 1;
        }
    },
    methods: {
        ...mapActions("finance/bankProfile", [
            "find",
            "update",
            "listAccounts",
            "destroyAccount"
        ]),
        async fetchFormData() {
            this.loading = true;
            try {
                await this.find(this.$route.params.id);
                this.setEditData();
                if (this.model.bank_id) {
                    await this.reloadAccounts();
                }
            } catch (error) {
                this.notice({
                    type: "error",
                    text: (error && error.message) || "Failed to load bank profile."
                });
            } finally {
                this.loading = false;
            }
        },
        setEditData() {
            if (!isEmpty(this.edit_data)) {
                this.model.bank_id = this.edit_data.bank_id;
                this.model.bank_name = this.edit_data.bank_name || "";
                this.originalBankName = this.model.bank_name;
                this.accounts = this.edit_data.accounts || [];
            }
        },
        async reloadAccounts() {
            this.loadingAccounts = true;
            try {
                const data = await this.listAccounts({
                    bankId: this.model.bank_id,
                    params: {}
                });
                this.accounts = data || [];
            } finally {
                this.loadingAccounts = false;
            }
        },
        onSaveBankName() {
            this.errors = new Errors();
            this.savingBank = true;
            this.update({
                id: this.model.bank_id,
                data: { bank_name: this.model.bank_name }
            })
                .then(response => {
                    this.notice({
                        type: response.message_id == 0 ? "success" : "warning",
                        text: response.message
                    });
                    if (response.message_id == 0) {
                        this.originalBankName = this.model.bank_name;
                    }
                })
                .catch(error => {
                    this.errors = new Errors(error.errors);
                    this.notice({ type: "error", text: error.message });
                })
                .finally(() => (this.savingBank = false));
        },
        onAddAccount() {
            this.editingAccount = null;
            this.accountModal = true;
        },
        onEditAccount(row) {
            this.editingAccount = row;
            this.accountModal = true;
        },
        onAccountSaved({ response, isEdit }) {
            this.notice({
                type: response && response.message_id == 0 ? "success" : "warning",
                text: (response && response.message) || (isEdit ? this.$t("updated") : this.$t("saved"))
            });
            this.reloadAccounts();
        },
        onDeleteAccount(row) {
            this.$set(row, "_deleting", true);
            this.destroyAccount(row.account_id)
                .then(response => {
                    this.notice({
                        type: response && response.message_id == 0 ? "success" : "warning",
                        text: (response && response.message) || this.$t("deleted")
                    });
                    this.reloadAccounts();
                })
                .catch(error =>
                    this.notice({ type: "error", text: error.message })
                )
                .finally(() => this.$set(row, "_deleting", false));
        },
        notice(not) {
            this.$Notice[not.type]({
                title: "BANK PROFILE",
                desc: not.text
            });
        }
    },
    beforeRouteEnter(to, from, next) {
        next(vm => vm.fetchFormData());
    },
    beforeRouteLeave(to, from, next) {
        AppOptions.appContentFullHeight = false;
        AppOptions.appContentClass = "";
        this.$store.commit("finance/bankProfile/RESET_STATE");
        next();
    }
};
</script>
